import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PlusIcon, PencilIcon, TrashIcon, XIcon, TagIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import { useStore, PromoCode } from '../../context/StoreContext';
const emptyPromo: Omit<PromoCode, 'id'> = {
  code: '',
  discountType: 'percentage',
  discountValue: 10,
  expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  isActive: true,
  applicableProductIds: []
};
export function AdminPromoCodes() {
  const {
    promoCodes,
    addPromoCode,
    editPromoCode,
    deletePromoCode,
    products
  } = useStore();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Omit<PromoCode, 'id'>>(emptyPromo);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const openAddForm = () => {
    setForm(emptyPromo);
    setEditingId(null);
    setShowForm(true);
  };
  const openEditForm = (promo: PromoCode) => {
    setForm({
      code: promo.code,
      discountType: promo.discountType,
      discountValue: promo.discountValue,
      expiryDate: promo.expiryDate.split('T')[0],
      isActive: promo.isActive,
      applicableProductIds: promo.applicableProductIds
    });
    setEditingId(promo.id);
    setShowForm(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Ensure expiry date is stored as full ISO string for end of that day
    const expiryDateObj = new Date(form.expiryDate);
    expiryDateObj.setHours(23, 59, 59, 999);
    const promoData: PromoCode = {
      id: editingId || `promo_${Date.now()}`,
      ...form,
      expiryDate: expiryDateObj.toISOString()
    };
    if (editingId) {
      editPromoCode(promoData);
    } else {
      addPromoCode(promoData);
    }
    setShowForm(false);
  };
  const toggleProductSelection = (productId: string) => {
    setForm(prev => {
      const current = prev.applicableProductIds;
      if (current.includes(productId)) {
        return {
          ...prev,
          applicableProductIds: current.filter(id => id !== productId)
        };
      } else {
        return {
          ...prev,
          applicableProductIds: [...current, productId]
        };
      }
    });
  };
  return <div className="space-y-6" data-id="element-661">
      <div className="flex items-center justify-between" data-id="element-662">
        <div data-id="element-663">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-664">Promo Codes</h1>
          <p className="text-sm text-slate-500" data-id="element-665">
            Manage discount codes and offers
          </p>
        </div>
        <button onClick={openAddForm} className="flex items-center gap-2 px-4 py-2.5 bg-sky-600 text-white text-sm font-medium rounded-lg hover:bg-sky-700 transition-colors" data-id="element-666">
          <PlusIcon className="w-4 h-4" data-id="element-667" /> Create Promo
        </button>
      </div>

      {/* Promo Codes List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-668">
        {promoCodes.map(promo => {
        const isExpired = new Date(promo.expiryDate) < new Date();
        const status = !promo.isActive ? 'Disabled' : isExpired ? 'Expired' : 'Active';
        const statusColor = status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600';
        return <motion.div key={promo.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className={`bg-white rounded-xl border p-5 ${!promo.isActive || isExpired ? 'border-slate-200 opacity-75' : 'border-sky-200 shadow-sm'}`} data-id="element-669">
              <div className="flex justify-between items-start mb-4" data-id="element-670">
                <div className="flex items-center gap-2" data-id="element-671">
                  <div className="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center" data-id="element-672">
                    <TagIcon className="w-4 h-4 text-sky-600" data-id="element-673" />
                  </div>
                  <div data-id="element-674">
                    <h3 className="font-bold text-lg text-slate-900 tracking-wider" data-id="element-675">
                      {promo.code}
                    </h3>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColor}`} data-id="element-676">
                      {status}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1" data-id="element-677">
                  <button onClick={() => openEditForm(promo)} className="p-1.5 text-slate-400 hover:text-sky-600 rounded-lg" data-id="element-678">
                    <PencilIcon className="w-4 h-4" data-id="element-679" />
                  </button>
                  <button onClick={() => setDeleteConfirm(promo.id)} className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg" data-id="element-680">
                    <TrashIcon className="w-4 h-4" data-id="element-681" />
                  </button>
                </div>
              </div>

              <div className="space-y-2 text-sm" data-id="element-682">
                <div className="flex justify-between text-slate-600" data-id="element-683">
                  <span data-id="element-684">Discount:</span>
                  <span className="font-bold text-slate-900" data-id="element-685">
                    {promo.discountType === 'percentage' ? `${promo.discountValue}% OFF` : `₹${promo.discountValue} OFF`}
                  </span>
                </div>
                <div className="flex justify-between text-slate-600" data-id="element-686">
                  <span data-id="element-687">Expires:</span>
                  <span data-id="element-688">
                    {new Date(promo.expiryDate).toLocaleDateString('en-IN')}
                  </span>
                </div>
                <div className="flex justify-between text-slate-600" data-id="element-689">
                  <span data-id="element-690">Applies to:</span>
                  <span data-id="element-691">
                    {promo.applicableProductIds.length === 0 ? 'All Products' : `${promo.applicableProductIds.length} Products`}
                  </span>
                </div>
              </div>
            </motion.div>;
      })}

        {promoCodes.length === 0 && <div className="col-span-full bg-white rounded-xl border border-slate-200 py-12 text-center" data-id="element-692">
            <TagIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" data-id="element-693" />
            <h3 className="text-lg font-medium text-slate-900 mb-1" data-id="element-694">
              No Promo Codes
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-695">
              Create your first promo code to offer discounts to customers.
            </p>
          </div>}
      </div>

      {/* Add/Edit Modal */}
      {showForm && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-696">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowForm(false)} data-id="element-697" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col" data-id="element-698">
            <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10" data-id="element-699">
              <h2 className="font-bold text-lg text-slate-900" data-id="element-700">
                {editingId ? 'Edit Promo Code' : 'Create Promo Code'}
              </h2>
              <button onClick={() => setShowForm(false)} className="p-2 text-slate-400 hover:bg-slate-100 rounded-lg" data-id="element-701">
                <XIcon className="w-5 h-5" data-id="element-702" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6" data-id="element-703">
              <form id="promo-form" onSubmit={handleSubmit} className="space-y-6" data-id="element-704">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-705">
                  <div className="sm:col-span-2" data-id="element-706">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-707">
                      Promo Code *
                    </label>
                    <input type="text" required value={form.code} onChange={e => setForm({
                  ...form,
                  code: e.target.value.toUpperCase().replace(/\s/g, '')
                })} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm font-bold tracking-wider focus:outline-none focus:ring-2 focus:ring-sky-500 uppercase" placeholder="e.g. SUMMER20" data-id="element-708" />
                  </div>

                  <div data-id="element-709">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-710">
                      Discount Type *
                    </label>
                    <select value={form.discountType} onChange={e => setForm({
                  ...form,
                  discountType: e.target.value as 'percentage' | 'fixed'
                })} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" data-id="element-711">
                      <option value="percentage" data-id="element-712">Percentage (%)</option>
                      <option value="fixed" data-id="element-713">Fixed Amount (₹)</option>
                    </select>
                  </div>

                  <div data-id="element-714">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-715">
                      Discount Value{' '}
                      {form.discountType === 'percentage' ? '(%)' : '(₹)'} *
                    </label>
                    <input type="number" required min="1" max={form.discountType === 'percentage' ? 100 : undefined} value={form.discountValue} onChange={e => setForm({
                  ...form,
                  discountValue: Number(e.target.value)
                })} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" data-id="element-716" />
                  </div>

                  <div data-id="element-717">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-718">
                      Expiry Date *
                    </label>
                    <input type="date" required min={new Date().toISOString().split('T')[0]} value={form.expiryDate} onChange={e => setForm({
                  ...form,
                  expiryDate: e.target.value
                })} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" data-id="element-719" />
                  </div>

                  <div className="flex items-center mt-6" data-id="element-720">
                    <label className="flex items-center gap-3 cursor-pointer" data-id="element-721">
                      <div className={`w-10 h-6 rounded-full transition-colors relative ${form.isActive ? 'bg-sky-500' : 'bg-slate-300'}`} data-id="element-722">
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${form.isActive ? 'left-5' : 'left-1'}`} data-id="element-723" />
                      </div>
                      <input type="checkbox" checked={form.isActive} onChange={e => setForm({
                    ...form,
                    isActive: e.target.checked
                  })} className="hidden" data-id="element-724" />
                      <span className="text-sm font-medium text-slate-700" data-id="element-725">
                        Active Status
                      </span>
                    </label>
                  </div>
                </div>

                <div data-id="element-726">
                  <div className="flex items-center justify-between mb-2" data-id="element-727">
                    <label className="block text-sm font-medium text-slate-700" data-id="element-728">
                      Applicable Products
                    </label>
                    <button type="button" onClick={() => setForm({
                  ...form,
                  applicableProductIds: []
                })} className="text-xs text-sky-600 hover:text-sky-700 font-medium" data-id="element-729">
                      Apply to All
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mb-3" data-id="element-730">
                    If no products are selected, the promo code will apply to
                    all products.
                  </p>

                  <div className="border border-slate-200 rounded-lg max-h-60 overflow-y-auto" data-id="element-731">
                    {products.map(product => {
                  const isSelected = form.applicableProductIds.includes(product.id);
                  return <div key={product.id} onClick={() => toggleProductSelection(product.id)} className={`flex items-center gap-3 p-3 border-b border-slate-100 last:border-0 cursor-pointer transition-colors ${isSelected ? 'bg-sky-50' : 'hover:bg-slate-50'}`} data-id="element-732">
                          <div className={`w-5 h-5 rounded border flex items-center justify-center ${isSelected ? 'bg-sky-500 border-sky-500' : 'border-slate-300'}`} data-id="element-733">
                            {isSelected && <CheckIcon className="w-3 h-3 text-white" data-id="element-734" />}
                          </div>
                          <img src={product.images[0]} alt={product.name} className="w-8 h-10 object-cover rounded bg-slate-100" data-id="element-735" />
                          <div className="flex-1 min-w-0" data-id="element-736">
                            <p className="text-sm font-medium text-slate-900 truncate" data-id="element-737">
                              {product.name}
                            </p>
                            <p className="text-xs text-slate-500" data-id="element-738">
                              ₹{product.sellingPrice}
                            </p>
                          </div>
                        </div>;
                })}
                  </div>
                </div>
              </form>
            </div>

            <div className="flex-shrink-0 flex gap-3 p-6 border-t border-slate-200 bg-slate-50 rounded-b-2xl" data-id="element-739">
              <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-3 border border-slate-200 bg-white text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors" data-id="element-740">
                Cancel
              </button>
              <button type="submit" form="promo-form" className="flex-1 py-3 bg-sky-600 text-white font-semibold rounded-lg hover:bg-sky-700 transition-colors" data-id="element-741">
                {editingId ? 'Update Promo Code' : 'Create Promo Code'}
              </button>
            </div>
          </motion.div>
        </div>}

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-742">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteConfirm(null)} data-id="element-743" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="relative bg-white rounded-xl p-6 max-w-sm w-full shadow-xl text-center" data-id="element-744">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-745">
              <XCircleIcon className="w-6 h-6 text-red-600" data-id="element-746" />
            </div>
            <h3 className="font-bold text-lg text-slate-900 mb-2" data-id="element-747">
              Delete Promo Code?
            </h3>
            <p className="text-sm text-slate-500 mb-6" data-id="element-748">
              This action cannot be undone. Customers will no longer be able to
              use this code.
            </p>
            <div className="flex gap-3" data-id="element-749">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-50" data-id="element-750">
                Cancel
              </button>
              <button onClick={() => {
            deletePromoCode(deleteConfirm);
            setDeleteConfirm(null);
          }} className="flex-1 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700" data-id="element-751">
                Delete
              </button>
            </div>
          </motion.div>
        </div>}
    </div>;
}